import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Code,
  Copy,
  Database,
  FileText,
  Globe,
  Grid3x3,
  List,
  Loader2,
  Lock,
  LockOpen,
  Mail,
  Phone,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { AgentFilters } from "@/components/agent/core/AgentFilters";
import {
  GenieChannelIndicators,
  getGenieChannels,
  IndicatorChip,
  MUTED_INDICATOR_COLOR,
} from "@/components/agent/GenieChannelIndicators";
import { GenieQuickTestModal } from "@/components/agent/modals/testing/GenieQuickTestModal";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Checkbox } from "@/components/ui/checkbox";
import { useAllAgents, useElDetails } from "@/hooks/agent";
import { AgentService, CloneOptions, type } from "@/services/agent/core";
import { Agent } from "@/types/agent/agent";
import { PendingBulkUpdate } from "@/types/agent/bulk-update";
import { Roles } from "@/types/auth/auth";
import { AgentSelectionToolbar } from "./AgentSelectionToolbar";
import { AssignToCustomerDialog } from "./AssignToCustomerDialog";
import { BulkUpdateProgressDialog } from "./BulkUpdateProgressDialog";
import { BulkUpdateSheet } from "./BulkUpdateSheet";
import { CloneAgentModal, type AssignableUser } from "./CloneAgentModal";
import { ConfirmBulkUpdateDialog } from "./ConfirmBulkUpdateDialog";
import { ElDetailsModal } from "./ElDetailsModal";

const INDICATOR_COLOR = "#4E9CFF";

/** One fixed-height line of phone chips (numbers in tooltips), so cards keep the same height. */
const GeniePhoneRow = ({ genie }: { genie: Agent }) => {
  const { phoneNumber, escalationNumbers } = getGenieChannels(genie);

  return (
    <div className="h-6 flex items-center">
      {phoneNumber || escalationNumbers.length > 0 ? (
        <GenieChannelIndicators genie={genie} brand={INDICATOR_COLOR} compact hideMailbox className="flex-nowrap" />
      ) : (
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-600">No Phone Number</span>
        </div>
      )}
    </div>
  );
};

/**
 * Description held to a fixed three-line box so every card stays the same
 * height. "more" turns the same box into a scroll area with the full text.
 */
const ExpandableDescription = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="relative h-[60px]">
      <p
        ref={textRef}
        className={`text-sm leading-5 text-gray-600 h-full ${expanded ? "overflow-y-auto pr-1 pb-5" : "line-clamp-3"}`}
      >
        {text}
      </p>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="absolute bottom-0 right-0 pl-6 pr-0.5 text-xs font-semibold hover:opacity-80 bg-gradient-to-r from-transparent via-white to-white"
        >
          <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
            {expanded ? "less" : "more"}
          </span>
        </button>
      )}
    </div>
  );
};

interface AgentListProps {
  onAgentClick: (agent: any) => void;
  refreshTrigger?: number;
  isSelectionMode?: boolean;
  selectedAgentIds?: Set<string>;
  onSelectionChange?: (selected: Set<string>) => void;
}

export const AgentList = ({
  onAgentClick,
  refreshTrigger,
  isSelectionMode = false,
  selectedAgentIds = new Set(),
  onSelectionChange,
}: AgentListProps) => {
  const [testAgent, setTestAgent] = useState<any | null>(null);
  const [assignAgent, setAssignAgent] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLlmModel, setSelectedLlmModel] = useState("");
  const [selectedVoiceLabel, setSelectedVoiceLabel] = useState("");
  const [selectedIsActive, setSelectedIsActive] = useState<boolean | "">("");
  const [selectedIsPublic, setSelectedIsPublic] = useState<boolean | "">("");
  const [selectedIsDemoGenie, setSelectedIsDemoGenie] = useState<boolean | "">("");
  const [selectedIsMarketplaceGenie, setSelectedIsMarketplaceGenie] = useState<boolean | "">("");
  const [selectedReportMode, setSelectedReportMode] = useState("");
  const [dailyCallLimit, setDailyCallLimit] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [agentToClone, setAgentToClone] = useState<any | null>(null);
  const [isCloningAgent, setIsCloningAgent] = useState(false);
  const [showElModal, setShowElModal] = useState(false);
  const [selectedAgentForEl, setSelectedAgentForEl] = useState<string | null>(null);
  const [showDbModal, setShowDbModal] = useState(false);
  const [selectedAgentDbData, setSelectedAgentDbData] = useState<any | null>(null);

  const { data: elData, isLoading: elLoading } = useElDetails(selectedAgentForEl);

  const handleCopyMailboxAddress = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Mailbox address copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const filters = {
    searchTerm: searchTerm || undefined,
    role: selectedRole || undefined,
    category: selectedCategory || undefined,
    llmModel: selectedLlmModel || undefined,
    voiceLabel: selectedVoiceLabel || undefined,
    isActive: selectedIsActive !== "" ? selectedIsActive : undefined,
    isPublic: selectedIsPublic !== "" ? selectedIsPublic : undefined,
    isDemoGenie: selectedIsDemoGenie !== "" ? selectedIsDemoGenie : undefined,
    isMarketplaceGenie: selectedIsMarketplaceGenie !== "" ? selectedIsMarketplaceGenie : undefined,
    reportMode: selectedReportMode || undefined,
    dailyCallLimit: dailyCallLimit || undefined,
  };

  const {
    data: agentsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useAllAgents(30, selectedUserId || undefined, filters);
  const agents: any[] = agentsData?.allAgents ?? [];
  const [viewMode, setViewMode] = useState<"compact" | "detailed">("detailed");
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [bulkUpdateJobId, setBulkUpdateJobId] = useState<number | null>(null);
  const [pendingBulkUpdate, setPendingBulkUpdate] = useState<PendingBulkUpdate | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const prevRefreshTrigger = useRef(refreshTrigger);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const forceRefresh = refreshTrigger !== prevRefreshTrigger.current;
    if (forceRefresh) {
      refetch();
    }
    prevRefreshTrigger.current = refreshTrigger;
  }, [refreshTrigger, refetch]);

  // Intersection observer for infinite scroll pagination
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreTriggerRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedRole("");
    setSelectedCategory("");
    setSelectedLlmModel("");
    setSelectedVoiceLabel("");
    setSelectedIsActive("");
    setSelectedIsPublic("");
    setSelectedIsDemoGenie("");
    setSelectedIsMarketplaceGenie("");
    setSelectedReportMode("");
    setDailyCallLimit(null);
    setSelectedUserId(null);
  };

  const handleToggleSelection = (agentId: string) => {
    const newSelection = new Set(selectedAgentIds);
    if (newSelection.has(agentId)) {
      newSelection.delete(agentId);
    } else {
      newSelection.add(agentId);
    }
    onSelectionChange?.(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedAgentIds.size === agents.length) {
      onSelectionChange?.(new Set());
    } else {
      const allAgentIds = new Set(agents.map((agent) => agent.id));
      onSelectionChange?.(allAgentIds);
    }
  };

  const handleCloneClick = (agent: any) => {
    setAgentToClone(agent);
    setIsCloneModalOpen(true);
  };

  const handleCloneConfirm = async (name: string, owner: AssignableUser, options: CloneOptions) => {
    if (!agentToClone) return;

    setIsCloningAgent(true);
    try {
      const { report } = await AgentService.duplicateAgent(agentToClone.id, name, undefined, owner.id, options);
      refetch();
      const account = owner.email || owner.full_name || owner.id;
      const failedDocuments = report?.knowledgeDocuments.failed ?? 0;
      const failedFiles = report?.resources?.failedDocuments ?? 0;
      if (failedDocuments + failedFiles > 0) {
        toast.warning(
          `"${name}" created in ${account}, but ${failedDocuments} knowledge base document(s) and ${failedFiles} resource file(s) could not be copied`,
        );
      } else {
        toast.success(`"${name}" created in ${account}`);
      }
    } catch (error) {
      console.error("Failed to clone agent:", error);
      toast.error(
        error instanceof Error && error.message ? `Failed to clone genie: ${error.message}` : "Failed to clone genie",
      );
    } finally {
      setIsCloningAgent(false);
      setIsCloneModalOpen(false);
      setAgentToClone(null);
    }
  };

  return (
    <div className="w-full">
      {isSelectionMode && (
        <AgentSelectionToolbar
          selectedCount={selectedAgentIds.size}
          totalCount={agents.length}
          onBulkUpdate={() => setIsBulkUpdateOpen(true)}
          onClearSelection={() => onSelectionChange?.(new Set())}
          onSelectAll={handleSelectAll}
        />
      )}
      <div className="mb-4">
        <AgentFilters
          searchTerm={searchTerm}
          selectedRole={selectedRole}
          selectedCategory={selectedCategory}
          selectedLlmModel={selectedLlmModel}
          selectedVoiceLabel={selectedVoiceLabel}
          selectedIsActive={selectedIsActive}
          selectedIsPublic={selectedIsPublic}
          selectedIsDemoGenie={selectedIsDemoGenie}
          selectedIsMarketplaceGenie={selectedIsMarketplaceGenie}
          selectedReportMode={selectedReportMode}
          selectedUserId={selectedUserId}
          dailyCallLimit={dailyCallLimit}
          onSearchChange={setSearchTerm}
          onRoleChange={setSelectedRole}
          onCategoryChange={setSelectedCategory}
          onLlmModelChange={setSelectedLlmModel}
          onVoiceLabelChange={setSelectedVoiceLabel}
          onIsActiveChange={setSelectedIsActive}
          onIsPublicChange={setSelectedIsPublic}
          onIsDemoGenieChange={setSelectedIsDemoGenie}
          onIsMarketplaceGenieChange={setSelectedIsMarketplaceGenie}
          onReportModeChange={setSelectedReportMode}
          onUserChange={setSelectedUserId}
          onDailyCallLimitChange={setDailyCallLimit}
          onClearFilters={handleClearFilters}
          showAdvancedFilters={true}
          actions={
            <div className="p-[2px] rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] w-fit">
              <div className="flex rounded-[10px] bg-white">
                {([
                  { mode: "compact", label: "Compact", Icon: Grid3x3 },
                  { mode: "detailed", label: "Detailed", Icon: List },
                ] as const).map(({ mode, label, Icon }, idx) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setViewMode(mode)}
                    className={`min-h-[44px] sm:min-h-0 px-3 py-2 flex items-center gap-2 transition-colors ${
                      viewMode === mode ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-50"
                    } ${idx === 0 ? "rounded-l-lg" : "rounded-r-lg"}`}
                    title={`${label} view`}
                    aria-pressed={viewMode === mode}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          }
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading genies...</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No genies found. Try adjusting your filters.</div>
      ) : (
        <>
          <div
            className={
              viewMode === "compact"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4"
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            }
          >
            {agents.map((agent) =>
              viewMode === "compact" ? (
                // Compact Card View
                <div
                  key={agent.id}
                  className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
                >
                  <div
                    className={`group/card rounded-[10px] h-full flex flex-col overflow-hidden relative ${isSelectionMode && selectedAgentIds.has(agent.id) ? "bg-genie-primary/10" : "bg-white"}`}
                  >
                    {isSelectionMode && (
                      <div className="absolute top-2 left-2 z-10">
                        <Checkbox
                          checked={selectedAgentIds.has(agent.id)}
                          onCheckedChange={() => handleToggleSelection(agent.id)}
                        />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-genie-primary/10 to-[#FF4DB8]/10 flex items-center justify-center">
                          {agent.branding?.logoUrl || agent.branding?.customAvatarUrl ? (
                            <img
                              src={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
                              alt={agent.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement?.classList.add("no-image");
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-genie-primary/20 to-purple-500/20">
                              <span className="text-lg font-bold text-genie-primary">
                                {agent.name?.charAt(0)?.toUpperCase() || "G"}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-[#222B45] truncate hover:text-genie-primary transition-colors">
                            {agent.name}
                          </h3>
                          {agent.brand && (
                            <p className="text-xs font-medium text-genie-primary truncate">{agent.brand}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            Owner:{" "}
                            <CopyableEmail
                              email={agent.owner?.full_name || agent.owner?.email}
                              fallback="N/A"
                              textClassName="truncate"
                            />
                          </p>
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 line-clamp-2 mt-2 min-h-[32px]">
                        {agent.description || "Voice genie ready to assist"}
                      </p>

                      <GenieChannelIndicators
                        genie={agent}
                        brand={INDICATOR_COLOR}
                        compact
                        className="mt-2"
                        leading={
                          <>
                            <IndicatorChip
                              label={`Conversation reports ${agent.send_conv_reports ? "on" : "off"}`}
                              tooltip={`Conversation reports ${agent.send_conv_reports ? "on" : "off"}`}
                              color={agent.send_conv_reports ? INDICATOR_COLOR : MUTED_INDICATOR_COLOR}
                              compact
                            >
                              <FileText className="w-3 h-3" />
                            </IndicatorChip>
                            <IndicatorChip
                              label={agent.is_public ? "Public" : "Private"}
                              tooltip={agent.is_public ? "Public" : "Private"}
                              color={INDICATOR_COLOR}
                              compact
                            >
                              {agent.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                            </IndicatorChip>
                          </>
                        }
                      />

                      <div className="flex gap-1.5 mt-3 opacity-100 sm:opacity-0 sm:group-hover/card:opacity-100 transition-opacity">
                        <button
                          className="flex-1 px-2 py-1.5 rounded-md bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white text-xs font-medium hover:opacity-90 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTestAgent(agent);
                          }}
                        >
                          Test
                        </button>
                        <button
                          className="flex-1 px-2 py-1.5 rounded-md bg-white border border-[#A674DB] text-xs font-medium hover:bg-[#FDF2F8] transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAgentClick(agent);
                          }}
                        >
                          <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                            Edit
                          </span>
                        </button>
                        <button
                          className="px-2 py-1.5 rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloneClick(agent);
                          }}
                          disabled={isCloningAgent}
                          title="Clone genie"
                          aria-label="Clone genie"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="px-2 py-1.5 rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignAgent(agent);
                          }}
                          title="Assign to customer"
                          aria-label="Assign to customer"
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Detailed Card View
                <div
                  key={agent.id}
                  className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
                >
                  <div
                    className={`rounded-[10px] h-full flex flex-col overflow-hidden relative isolate ${isSelectionMode && selectedAgentIds.has(agent.id) ? "bg-genie-primary/10" : "bg-white"}`}
                  >
                    {/* Faded logo watermark bleeding off the top-left corner, behind all content. */}
                    {(agent.branding?.logoUrl || agent.branding?.customAvatarUrl) && (
                      <img
                        src={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
                        alt=""
                        aria-hidden
                        draggable={false}
                        className="absolute -top-10 -left-12 -z-10 w-64 h-64 object-contain opacity-[0.12] pointer-events-none select-none"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    {isSelectionMode && (
                      <div className="absolute top-4 left-4 z-20">
                        <Checkbox
                          checked={selectedAgentIds.has(agent.id)}
                          onCheckedChange={() => handleToggleSelection(agent.id)}
                        />
                      </div>
                    )}
                    <div className="relative p-4 pb-3 border-b border-gray-100">
                      <div className="absolute top-3 left-3 z-10">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-white shadow-sm flex items-center justify-center">
                          {agent.branding?.logoUrl || agent.branding?.customAvatarUrl ? (
                            <img
                              src={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
                              alt={agent.name}
                              className="w-full h-full object-contain p-1"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement?.classList.add("no-image");
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-genie-primary/20 to-purple-500/20">
                              <span className="text-lg font-bold text-genie-primary">
                                {agent.name?.charAt(0)?.toUpperCase() || "G"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="min-h-[72px] pt-1">
                          <div className="float-left w-12 h-12 mr-3"></div>
                          <h3 className="text-lg font-bold text-[#222B45] hover:text-genie-primary transition-colors leading-tight line-clamp-2">
                            {agent.name}
                          </h3>
                          {agent.brand && <p className="text-sm font-medium text-genie-primary">{agent.brand}</p>}
                        </div>

                        <div className="space-y-1 clear-both">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs text-gray-500">Owner:</p>
                            <div className="flex items-center gap-2 relative group">
                              <CopyableEmail
                                email={agent.owner?.full_name || agent.owner?.email}
                                fallback="N/A"
                                className="text-xs text-gray-500"
                              />
                              {agent.owner?.role && (
                                <div>
                                  <div
                                    className={`w-3 h-3 rounded-full hover:cursor-help ${
                                      {
                                        [Roles.Admin]: "bg-purple-500",
                                        [Roles.Business]: "bg-blue-500",
                                        [Roles.Consumer]: "bg-green-500",
                                      }[agent.owner.role] ?? "bg-gray-500"
                                    }`}
                                    title={
                                      {
                                        [Roles.Admin]: "Admin",
                                        [Roles.Business]: "Business",
                                        [Roles.Consumer]: "Consumer",
                                      }[agent.owner.role] ?? agent.owner.role
                                    }
                                  ></div>
                                  <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-100 text-genie-primary border border-genie-primary text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                                    {{
                                      [Roles.Admin]: "Admin",
                                      [Roles.Business]: "Business",
                                      [Roles.Consumer]: "Consumer",
                                    }[agent.owner.role] ?? agent.owner.role}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 p-4 space-y-3">
                      <ExpandableDescription
                        text={
                          agent.description ||
                          "A voice genie ready to assist your customers with natural conversations and instant responses."
                        }
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <div
                          className="flex items-center gap-2"
                          title={`Conversation reports ${agent.send_conv_reports ? "on" : "off"}`}
                        >
                          <FileText
                            className={`h-4 w-4 ${agent.send_conv_reports ? "text-[#A674DB]" : "text-gray-400"}`}
                          />
                          <span className="text-xs text-gray-600">
                            Reports {agent.send_conv_reports ? "on" : "off"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {agent.is_public ? (
                            <LockOpen className="h-4 w-4 text-genie-secondary" />
                          ) : (
                            <Lock className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-xs text-gray-600">{agent.is_public ? "Public" : "Private"}</span>
                        </div>
                      </div>

                      <div
                        className="flex items-center gap-2"
                        title={agent.mailbox?.status === "provisioned" ? agent.mailbox.email_address : undefined}
                      >
                        <Mail
                          className={`h-4 w-4 ${agent.mailbox?.status === "provisioned" ? "text-genie-primary" : "text-gray-400"}`}
                        />
                        <span className="text-xs text-gray-600 truncate">
                          <CopyableEmail
                            email={agent.mailbox?.status === "provisioned" ? agent.mailbox.email_address : null}
                            fallback={agent.mailbox?.status === "failed" ? "Mailbox Failed" : "No Mailbox"}
                            textClassName="truncate"
                          />
                        </span>
                      </div>

                      <GeniePhoneRow genie={agent} />
                    </div>

                    {/* Footer — pinned to the bottom so every card ends the same way. */}
                    <div className="border-t border-gray-100 pt-3">
                      {agent.elevenlabs_id && (
                        <div className="p-4 pt-0 pb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200 flex-1 truncate text-gray-700">
                              {agent.elevenlabs_id}
                            </span>
                            <button
                              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAgentForEl(agent.elevenlabs_id);
                                setShowElModal(true);
                              }}
                              title="View ElevenLabs details"
                            >
                              <Code className="h-4 w-4 text-gray-600" />
                            </button>
                            <button
                              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAgentDbData(agent);
                                setShowDbModal(true);
                              }}
                              title="View raw genie data"
                            >
                              <Database className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="p-4 pt-0 space-y-2">
                        <div className="flex gap-2">
                          <button
                            className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition text-center text-sm relative group"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignAgent(agent);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mx-auto" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-100 text-genie-primary border border-genie-primary text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                              Assign to Customer
                            </div>
                          </button>
                          <button
                            className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white font-medium hover:opacity-90 transition text-center text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTestAgent(agent);
                            }}
                          >
                            Test Genie
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button
                            className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 transition text-center text-sm disabled:opacity-50 relative group"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneClick(agent);
                            }}
                            disabled={isCloningAgent}
                          >
                            <Copy className="h-4 w-4 mx-auto" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-100 text-genie-primary border border-genie-primary text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                              Clone Genie
                            </div>
                          </button>
                          <button
                            className="flex-1 px-3 py-2 rounded-lg bg-white border border-[#A674DB] font-medium hover:bg-[#FDF2F8] transition text-center text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAgentClick(agent);
                            }}
                          >
                            <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
                              View & Edit
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Infinite scroll loading indicator */}
          {hasNextPage && (
            <div ref={loadMoreTriggerRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading more genies...</span>
                </div>
              )}
            </div>
          )}

          {/* Show total count */}
          <div className="text-center py-4 text-sm text-gray-500">Showing {agents.length} genies</div>
        </>
      )}
      {testAgent && <GenieQuickTestModal genie={testAgent} isOpen={!!testAgent} onClose={() => setTestAgent(null)} />}

      {assignAgent && (
        <AssignToCustomerDialog
          agent={assignAgent}
          isOpen={!!assignAgent}
          onClose={() => setAssignAgent(null)}
          onAssignmentUpdate={() => {
            refetch();
            setAssignAgent(null);
          }}
        />
      )}

      <CloneAgentModal
        isOpen={isCloneModalOpen}
        onClose={() => {
          setIsCloneModalOpen(false);
          setAgentToClone(null);
        }}
        agent={agentToClone}
        onConfirm={handleCloneConfirm}
        isLoading={isCloningAgent}
      />

      {/* Bulk Update Sheet */}
      <BulkUpdateSheet
        isOpen={isBulkUpdateOpen}
        selectedAgentIds={selectedAgentIds}
        onClose={() => setIsBulkUpdateOpen(false)}
        onApplyUpdates={(pending) => {
          setPendingBulkUpdate(pending);
          setIsConfirmOpen(true);
        }}
      />

      {/* Confirm Bulk Update Dialog */}
      <ConfirmBulkUpdateDialog
        isOpen={isConfirmOpen}
        selectedAgentIds={selectedAgentIds}
        pending={pendingBulkUpdate}
        onClose={() => {
          setIsConfirmOpen(false);
          setPendingBulkUpdate(null);
          setIsBulkUpdateOpen(false);
        }}
        onJobCreated={(jobId) => {
          setBulkUpdateJobId(jobId);
          setIsConfirmOpen(false);
          setPendingBulkUpdate(null);
        }}
        onQueuedJobCreated={() => {
          setIsConfirmOpen(false);
          setPendingBulkUpdate(null);
          setIsBulkUpdateOpen(false);
          onSelectionChange?.(new Set());
        }}
      />

      {/* Bulk Update Progress Dialog */}
      <BulkUpdateProgressDialog
        isOpen={bulkUpdateJobId !== null}
        jobId={bulkUpdateJobId}
        onClose={() => {
          setBulkUpdateJobId(null);
          refetch();
        }}
      />

      {/* ElevenLabs Details Modal */}
      <ElDetailsModal
        isOpen={showElModal}
        onClose={() => {
          setShowElModal(false);
          setSelectedAgentForEl(null);
        }}
        data={elData}
        isLoading={elLoading}
      />

      {/* Raw DB Data Modal */}
      <ElDetailsModal
        isOpen={showDbModal}
        onClose={() => {
          setShowDbModal(false);
          setSelectedAgentDbData(null);
        }}
        data={selectedAgentDbData}
        isLoading={false}
        title="Genie Raw Data"
      />
    </div>
  );
};
